/**
 * The NCR's lifecycle at a glance (spec, 24 Sep 2026): one card per REQUIRED
 * process with its sub-status and who acts next, then Close.
 *
 * Replaces both old steppers: the 4-step Created > CA > CAR > Close bar
 * described the 8D alone and named the wrong owner at every step (review C1),
 * and the "Template 2" emoji strip only rendered for one tenant setting that is
 * now retired. Everything shown is server-computed (`process_plate`,
 * `outstanding_processes`), the same rules the Close action enforces, so the
 * Close button appears when nothing is outstanding (the server also approves
 * a last submitted CAR as it closes).
 */
import type { NonConformanceReport } from '../../lib/api/types/quality';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Lock } from 'lucide-react';
import { NCRStateBadge, OwnerMark, PLATE_LABELS, daysWaitingText, type PlateKey } from '../../components/quality/ncrPlate';
import { cn } from '../../lib/utils';

interface NCRLifecyclePanelProps {
  ncr: NonConformanceReport;
  /** The viewer can act for the customer (closing is the customer's). */
  canActAsCustomer: boolean;
  onCloseClick: () => void;
}

const ORDER: PlateKey[] = ['eightd', 'collection', 'credit', 'replacement'];

export default function NCRLifecyclePanel({ ncr, canActAsCustomer, onCloseClick }: NCRLifecyclePanelProps) {
  const plate = ncr.process_plate;
  if (!plate) return null;
  const required = ORDER.filter((k) => plate[k] && plate[k].label !== 'Not required');
  const outstanding = ncr.outstanding_processes ?? [];
  const isOpen = !['draft', 'closed', 'deleted'].includes(ncr.status);
  const closable = isOpen && outstanding.length === 0;
  const dw = ncr.days_waiting;
  const counterLines = (k: PlateKey): string[] => {
    if (!dw) return [];
    const lines: (string | null)[] = k === 'eightd'
      ? [
          dw.containment ? `Containment: ${daysWaitingText(dw.containment)}` : null,
          dw.corrective_action ? `CAR: ${daysWaitingText(dw.corrective_action)}` : null,
        ]
      : k === 'collection' ? [daysWaitingText(dw.collection)]
      : k === 'credit' ? [daysWaitingText(dw.credit)]
      : [];
    return lines.filter((l): l is string => !!l);
  };

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#3f4254]">NCR status</span>
            <NCRStateBadge state={ncr.ncr_state} />
          </div>
          {closable && canActAsCustomer && (
            <Button type="button" size="sm" onClick={onCloseClick}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Close NCR
            </Button>
          )}
          {isOpen && !closable && (
            <span className="inline-flex items-center text-xs text-muted-foreground">
              <Lock className="mr-1 h-3.5 w-3.5" />
              Closes once {outstanding.join(', ')} {outstanding.length === 1 ? 'is' : 'are'} complete
            </span>
          )}
        </div>

        <ol className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {required.length === 0 && (
            <li className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground sm:col-span-2 lg:col-span-4">
              No 8D, collection, credit or replacement is required.
            </li>
          )}
          {required.map((k) => {
            const cell = plate[k];
            const done = !!cell.done;
            return (
              <li
                key={k}
                className={cn(
                  'rounded-md border px-3 py-2',
                  done
                    ? 'border-emerald-200 bg-emerald-50/60'
                    : cell.owner === 'S'
                      ? 'border-amber-300 bg-amber-50/60'
                      : cell.owner === 'C'
                        ? 'border-sky-300 bg-sky-50/60'
                        : 'border-gray-200',
                )}
              >
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {PLATE_LABELS[k]}
                </div>
                <div className="mt-0.5 flex items-center text-sm font-medium text-[#3f4254]">
                  {done && <CheckCircle2 className="mr-1 h-4 w-4 text-emerald-600" />}
                  {cell.label}
                  <OwnerMark owner={cell.owner} />
                </div>
                {cell.owner && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {cell.owner === 'S' ? 'Supplier to act' : 'Customer to act'}
                  </div>
                )}
                {/* Legacy parity: the days-waiting counters, on the card they time. */}
                {counterLines(k).map((line) => (
                  <div key={line} className="mt-0.5 text-xs tabular-nums text-muted-foreground">{line}</div>
                ))}
              </li>
            );
          })}
          <li
            className={cn(
              'rounded-md border px-3 py-2',
              ncr.status === 'closed'
                ? 'border-emerald-300 bg-emerald-600 text-white'
                : closable
                  ? 'border-sky-300 bg-sky-50/60'
                  : 'border-gray-200 text-muted-foreground',
            )}
          >
            <div className={cn('text-[11px] font-semibold uppercase tracking-wide',
              ncr.status === 'closed' ? 'text-emerald-50' : 'text-muted-foreground')}>
              Close
            </div>
            <div className="mt-0.5 flex items-center text-sm font-medium">
              {ncr.status === 'closed' ? 'Closed' : closable ? 'Ready to close' : 'Waiting'}
              {closable && <OwnerMark owner="C" />}
            </div>
          </li>
        </ol>
      </CardContent>
    </Card>
  );
}
